/**
 * `{{variable}}` substitution for the gRPC and WebSocket builders — what each
 * field becomes on the way to Rust. The resolver itself is the one HTTP uses
 * (utils/scopedVariableResolver); this module only says WHICH fields go
 * through it, so the builders stay call sites.
 *
 * Fields, as Postman does it for these request kinds:
 *   * gRPC      — server address, metadata keys and values, the message JSON
 *                 (substituted as text, like a raw HTTP body: `{"id": {{id}}}`
 *                 is fine when `id` is a number).
 *   * WebSocket — url, handshake header keys and values, and every outgoing
 *                 message (Postman resolves variables in WebSocket messages).
 *
 * Unresolved references stay literal, as on HTTP. Secrets are substituted in
 * JS, as on HTTP; what a LOG shows keeps a secret as `{{name}}` (`shown*`).
 */
import { extractVariableNames } from './resolveVariables';
import { createScopedVariableResolver, involvesSecret, logSafeText } from './scopedVariableResolver';

const str = (v) => (v === null || v === undefined ? '' : String(v));

/**
 * The gRPC call's substituted fields. Metadata rows without a key, or switched
 * off, are not sent (as before); the rest become `[key, value]` pairs.
 *
 * @param {{address?: string, requestJson?: string, metadata?: object[]}} fields
 * @param {object} scopes  stored scopes (utils/variablePrecedence keys)
 */
export function prepareGrpcCall({ address, requestJson, metadata } = {}, scopes = {}) {
  const substitute = createScopedVariableResolver(scopes);
  return {
    address: str(substitute(str(address))).trim(),
    requestJson: str(substitute(str(requestJson))),
    metadata: (metadata || [])
      .filter((m) => str(m?.key).trim() && m.enabled !== false)
      .map((m) => [str(substitute(m.key)).trim(), str(substitute(str(m.value)))]),
  };
}

/**
 * A WebSocket connect: the substituted url and handshake headers, plus what the
 * log may show for them and the names nothing defines.
 *
 * @returns {{url: string, displayUrl: string, headers: Record<string,string>,
 *            shownHeaders: Record<string,string>, unresolved: string[]}}
 */
export function prepareWsConnect({ url, headers } = {}, scopes = {}) {
  const substitute = createScopedVariableResolver(scopes);
  const template = str(url).trim();
  const sentUrl = str(substitute(template)).trim();
  const out = {};
  const shownHeaders = {};
  for (const h of headers || []) {
    if (h?.enabled === false || !str(h?.key).trim()) continue;
    const name = str(substitute(h.key.trim())).trim();
    const value = str(substitute(str(h.value)));
    out[name] = value;
    if (involvesSecret(str(h.value), scopes)) shownHeaders[name.toLowerCase()] = logSafeText(str(h.value), value, scopes);
  }
  return {
    url: sentUrl,
    displayUrl: logSafeText(template, sentUrl, scopes),
    headers: out,
    shownHeaders,
    unresolved: extractVariableNames(sentUrl),
  };
}

/** An outgoing WebSocket message: what is sent, and what its log row shows. */
export function prepareWsMessage(text, scopes = {}) {
  const sent = str(createScopedVariableResolver(scopes)(str(text)));
  return { text: sent, shown: logSafeText(str(text), sent, scopes) };
}
